/**
 * Given an element, traverses through it's parents to find something that matches the given selector.
 *
 * @param {HTMLElement} element - The element to start traversing from
 * @param {String} parentSelector - The selector to match against
 *
 * @returns {(HTMLElement|boolean)} - The container if found, false otherwise
 */
export function getParentElement(element, parentSelector) {
  let container = element;
  while(!container.matches(parentSelector)) {
    container = container.parentNode;
    if(container === document.documentElement) return false;
  }
  return container;
}

/**
 * Given a kebab-case-string, returns a camelCaseString
 * @param {String} kebab - The kebab-case string to be converted
 * @returns {String} - Camel-case string
 */
export function kebabToCamel(kebab) {
  return kebab.replace(/[-_](.)/g, (_, c) => c.toUpperCase());
}

/**
 * Given a camelCaseString, returns a kebab-case-string
 * @param {String} camel - The camel-case string to be converted
 * @returns {String} - Kebab-case string
 */
export function camelToKebab(camel) {
  return camel.replace(/[^a-z]/ig, '').
    replace(/^[A-Z]/, c => c.toLowerCase()).
    replace(/[A-Z]/g, c => `-${c.toLowerCase()}`);
}

/**
 * Converts a data attribute into a key that can be used with HTMLElement.dataset
 * @param {String} dataAttr - The data attribute string, e.g. 'data-coffee-roast-level'
 * @throws - Will throw an error if input doesn't look like a data-attribute
 * @returns {String} - The converted dataset key, e.g. 'coffeeRoastLevel'
 */
export function dataAttrToKey(dataAttr) {
  if(dataAttr.indexOf('data-') !== 0) {
    throw new Error('Expected a data attribute, got ' + JSON.stringify(dataAttr));
  }
  return kebabToCamel(dataAttr.slice(5));
}

/**
 * Converts a dataset key into a data-attribute string
 * @param {String} dataKey - The dataset key, e.g. 'coffeeRoastLevel'
 * @returns {String} - The converted data-attribute string, e.g. 'data-coffee-roast-level'
 */
export function dataKeyToAttr(dataKey) {
  return 'data-' + camelToKebab(dataKey);
}

export function triggerEvent(target, eventType) {
  const event = new Event(eventType, { bubbles: true });
  target.dispatchEvent(event);
}


/**
 * Helper function to force a link to open in a new window, and not a new tab.
 *
 * @param {String} url - The address that should get opened within the new window
 *
 * @returns {Object} - A reference to the window object of the opened popup
 */
export function openInNewWindow(url) {
  return window.open(url, 'popup-window', 'menubar=no');
}



export default {
  getParentElement,
  kebabToCamel,
  camelToKebab,
  dataAttrToKey,
  dataKeyToAttr,
  triggerEvent,
};
